import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { LocateFixed } from "lucide-react";
import { toast } from "sonner";

export const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
export const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
export const US_CENTER = [37.5, -96];
const dropIcon = L.divIcon({ className: "", html: '<div class="drop-pin"></div>', iconSize: [22, 22], iconAnchor: [11, 22] });

function ClickToPin({ onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: +e.latlng.lat.toFixed(5), lng: +e.latlng.lng.toFixed(5) }) });
  return null;
}

export function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom ?? Math.max(map.getZoom(), 12));
  }, [center?.[0], center?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function useMyLocation() {
  const [busy, setBusy] = useState(false);
  const locate = (onFound) => {
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setBusy(false); onFound({ lat: +p.coords.latitude.toFixed(5), lng: +p.coords.longitude.toFixed(5) }); },
      () => { setBusy(false); toast.error("Couldn't get your location — tap the map instead"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  return { busy, locate };
}

export function MapPicker({ value, onChange, height = 260, testId = "map-picker" }) {
  const { busy, locate } = useMyLocation();
  const center = value ? [value.lat, value.lng] : null;
  return (
    <div className="space-y-2">
      <div className="border-2 border-[#27272A] relative" style={{ height }} data-testid={testId}>
        <MapContainer center={center || US_CENTER} zoom={center ? 12 : 4} className="h-full w-full">
          <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
          <ClickToPin onPick={onChange} />
          <Recenter center={center} />
          {center && <Marker position={center} icon={dropIcon} />}
        </MapContainer>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-zinc-400">
        <span data-testid={`${testId}-coords`}>
          {value ? `${value.lat}, ${value.lng}` : "Tap the map to drop a pin"}
        </span>
        <button type="button" onClick={() => locate(onChange)} disabled={busy} data-testid={`${testId}-locate-btn`}
          className="flex items-center gap-1 border-2 border-[#F4F4F5] px-3 py-2 font-bold uppercase hover:bg-white hover:text-black transition-colors">
          <LocateFixed size={14} /> {busy ? "Locating…" : "Use my location"}
        </button>
      </div>
    </div>
  );
}
